/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ParsedCensusResponse, CensusData } from '@/mrRogers/interfaces';
import { DataCard } from '@/reports/index';
import { Stack, Text, styled, Table } from '@/theme/index';
import { capitalizeString } from '@/utils/index';
import { calculatePcts, toPercentage, calculateTotals } from '../utils/utils';

interface OccupationProps {
  /** Parsed Data from Mr.Rogers Response */
  mrRogersData?: ParsedCensusResponse;
}

const StyledTable = styled(Table)(({ theme }) => ({
  thead: {
    th: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      fontSize: theme.spacing(2),
      fontWeight: '700',
    },
  },

  tbody: {
    tr: {
      'td:first-of-type': {
        fontWeight: 'bold',
      },
      td: {
        fontSize: theme.spacing(2),
        padding: theme.spacing(2),
      },
    },
  },
}));

/** Occupation component shows to the user the Occupation values with percentage
 * categorized by blue collar and white collar based on the property searched
 */

export const Occupation = ({ mrRogersData }: OccupationProps) => {
  interface OccupationDataItem {
    values: (string | number)[];
  }

  const occupationTableData = (): {
    headers: string[];
    data: OccupationDataItem[];
  } => {
    const fields = ['bcoll', 'wcoll'];

    const census = {
      local: mrRogersData?.local as CensusData,
      county: mrRogersData?.county as CensusData,
      state: mrRogersData?.state as CensusData,
    };

    const totals = calculateTotals(fields, census);

    const bPcts = calculatePcts(fields[0], totals, census);
    const wPcts = calculatePcts(fields[1], totals, census);

    const data: OccupationDataItem[] = [];

    Object.keys(census).forEach((key) => {
      if (
        bPcts[key as keyof typeof census] ||
        wPcts[key as keyof typeof census]
      ) {
        data.push({
          values: [
            capitalizeString(key),
            toPercentage(bPcts[key as keyof typeof census]!),
            toPercentage(wPcts[key as keyof typeof census]!),
          ],
        });
      }
    });

    const occupationTableDataNullState: OccupationDataItem[] = [
      { values: ['Local', '-', '-'] },
      { values: ['County', '-', '-'] },
      { values: ['State', '-', '-'] },
    ];

    if (data.length) {
      return { headers: ['Level', 'Blue collar', 'White collar'], data };
    }
    return {
      headers: ['Level', 'Blue collar', 'White collar'],
      data: occupationTableDataNullState,
    };
  };

  return (
    <DataCard>
      <Stack>
        <Text variant="h3" mb={3.125}>
          Occupation
        </Text>
        <StyledTable
          data-testid="occupation-table"
          className="striped"
          headers={occupationTableData().headers}
          rows={occupationTableData().data.map((item) =>
            item.values.map((value) =>
              value ? (value as string | JSX.Element) : '-'
            )
          )}
        />
      </Stack>
    </DataCard>
  );
};
